import type { TFunction } from 'i18next'
import { ApiError } from '@/core/infra_api_client'

/**
 * Page-owned text for the rollout routes' stable error codes (organisation
 * routes and the staff routes of PLAN-20260930 slice 5). The server decides
 * every refusal; the page only translates its code and never shows the
 * backend's own message.
 */
export const rolloutRefusalKeys: Readonly<Record<string, string>> = {
  staff_claim_required: 'staffClaimRequired',
  staff_check_unavailable: 'staffCheckUnavailable',
  staff_settings_unavailable: 'staffSettingsUnavailable',
  staff_rollout_card_unknown: 'cardUnknown',
  staff_rollout_organization_not_found: 'organizationNotFound',
  staff_rollout_organization_inactive: 'organizationInactive',
  staff_rollout_bad_request: 'badRequest',
  staff_rollout_confirmation_required: 'confirmationRequired',
  staff_rollout_audit_unavailable: 'auditUnavailable',
  staff_rollout_outcome_uncertain: 'outcomeUncertain',
  staff_rollout_unavailable: 'unavailable',
  mission_stack_rollout_environment_locked: 'missionStackEnvironmentLocked',
  mission_stack_rollout_invalid: 'missionStackInvalid',
  mission_stack_rollout_unavailable: 'missionStackUnavailable',
  ui_variant_rollout_environment_locked: 'uiVariantEnvironmentLocked',
  'ui_variant.governance_classified_sources': 'uiVariantClassifiedSources',
  'ui_variant.governance_adoption_started': 'uiVariantAdoptionStarted',
  ui_variant_rollout_invalid: 'uiVariantInvalid',
  ui_variant_rollout_unavailable: 'uiVariantUnavailable',
  settings_rollout_invalid: 'settingsInvalid',
  settings_rollout_unavailable: 'settingsUnavailable',
  managed_chat_address_invalid: 'managedChatInvalid',
  managed_chat_address_unavailable: 'managedChatUnavailable',
}

const revisionConflictCodes: ReadonlySet<string> = new Set([
  'ui_variant_rollout_revision_conflict',
  'mission_stack_rollout_revision_conflict',
  'settings_rollout_revision_conflict',
  'managed_chat_address_revision_conflict',
])

// staff_settings_unavailable: infra-service's staff entry got no answer from
// governance, so a forwarded change may or may not have been saved.
const uncertainOutcomeCodes: ReadonlySet<string> = new Set([
  'staff_rollout_outcome_uncertain',
  'staff_settings_unavailable',
])

/**
 * True when the server could neither confirm nor rule out a staff change's
 * commit: the change may have taken effect, so the card reads the policy
 * again instead of inviting a blind retry.
 */
export function isRolloutOutcomeUncertain(error: unknown): boolean {
  return error instanceof ApiError && error.code !== undefined && uncertainOutcomeCodes.has(error.code)
}

/** A failure whose text the page wrote itself, such as an organisation mismatch. */
export class RolloutPageError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'RolloutPageError'
  }
}

/** True when the server refused a write because the policy revision moved on. */
export function isRolloutRevisionConflict(error: unknown): boolean {
  return error instanceof ApiError && error.code !== undefined && revisionConflictCodes.has(error.code)
}

/**
 * The text a rollout card shows for a failure: the translated refusal for a
 * known code, the page's own text for a RolloutPageError, otherwise the
 * caller's fallback. Backend messages are never shown.
 */
export function rolloutErrorText(error: unknown, t: TFunction<'pages'>, fallbackKey: string): string {
  if (error instanceof RolloutPageError) return error.message
  if (error instanceof ApiError) {
    const key = error.code ? rolloutRefusalKeys[error.code] : undefined
    if (key) return t(`rolloutControls.refusals.${key}`)
    if (error.status === 401) return t('rolloutControls.refusals.signInRequired')
  }
  return t(fallbackKey)
}
